import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Link,
} from '@mui/material';
import { Transaction, getTransactions } from './paymentApi';

const statusColors: Record<
  string,
  'success' | 'error' | 'warning' | 'default'
> = {
  succeeded: 'success',
  failed: 'error',
  canceled: 'error',
  requires_payment_method: 'warning',
  requires_action: 'warning',
  processing: 'default',
};

const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // @ts-ignore
  const orgId: string | undefined = (global as any)?.me?.orgID;

  const fetchTransactions = useCallback(async () => {
    if (!orgId) {
      setError('Organization not found');
      setLoading(false);
      return;
    }
    try {
      setError(null);
      setLoading(true);
      const res = await getTransactions(orgId);
      if (res.success && res.data) {
        setTransactions(res.data.transactions);
      } else {
        setError(res.error || 'Failed to load transactions');
      }
    } catch {
      setError('Unable to load transactions. Check your connection.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchTransactions}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {transactions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No transactions yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Transactions will appear here after estimates are purchased.
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Receipt</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((tx) => (
                <TableRow key={tx.id}>
                  <TableCell>{formatDate(tx.created)}</TableCell>
                  <TableCell>{tx.description}</TableCell>
                  <TableCell align="right">
                    ${tx.amount.toFixed(2)} {tx.currency}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={tx.status.replace(/_/g, ' ')}
                      size="small"
                      color={statusColors[tx.status] || 'default'}
                      variant="filled"
                    />
                  </TableCell>
                  <TableCell>
                    {tx.receiptUrl ? (
                      <Link href={tx.receiptUrl} target="_blank" rel="noopener">
                        View
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default Transactions;
